import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { marketOverviewKeys } from '@/features/market-overview/api/keys'
import { overviewService } from '@/features/market-overview/api/overviewService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { IndexRange } from '@/schemas/market'
import type { MarketOverview } from '@/schemas/market-overview'

/**
 * The range the Overview's index panel opens its session chart on, for the
 * `/market` loader to start that read under the same key.
 *
 * A copy of `OVERVIEW_INDEX_DEFAULT_RANGE`, which OverviewIndexPanel still
 * declares for itself: a loader must not import a component module, or the
 * component and everything it draws with (lightweight-charts included) move
 * into the entry chunk, since route loaders are not code-split. If the two
 * ever disagree the cost is one wasted prefetch — the panel fetches its own
 * key regardless — but the panel should import this one instead; that edit is
 * outside this change.
 */
export const INDEX_PANEL_DEFAULT_RANGE: IndexRange = '1w'

/**
 * The whole Market · Overview tab in one query — the route's MAIN data: the
 * `/market` loader awaits it, so the screen and every card on it mount with it
 * already cached.
 *
 * The factory carries the key and the fetch and nothing else. Polling lives in
 * the hooks below because it is gated on `useMarketStatus()`, a hook; the
 * loader fetches once and the screen's hook takes over from there.
 *
 * `({ client })`: React Query hands the queryFn the client that owns this
 * query, so the fan-out's endpoint entries land on that client — the one the
 * loader was given through router context — rather than on the module
 * singleton; see `marketEndpoints` in api/endpointCache.ts.
 */
export const marketOverviewQueryOptions = () =>
  queryOptions<MarketOverview>({
    queryKey: marketOverviewKeys.overview(),
    queryFn: ({ client }) => overviewService.overview(client),
  })

/**
 * The screen's read of it: data is always defined, because the loader has
 * already put it in the cache (and a first-load failure never reaches the
 * component — the loader throws to the route's error component instead).
 *
 * Polling is gated on market status exactly as `useIndexSummary` is, so a tab
 * left open overnight issues no requests at all — none of these figures can
 * move while NEPSE is shut. A poll that fails keeps the data on screen: a
 * suspense query only throws while it has none.
 */
export const useSuspenseMarketOverview = (): UseSuspenseQueryResult<MarketOverview> => {
  const { isOpen } = useMarketStatus()

  return useSuspenseQuery({ ...marketOverviewQueryOptions(), refetchInterval: livePolling(isOpen) })
}

/**
 * The same query for the cards on the tab, which read it for themselves (see
 * OverviewScreen). Not a suspense read: under the route's loader their data is
 * always there on mount, so the loading and failed branches they still carry
 * are unreachable on `/market` — kept only because `OverviewIndexPanel` and
 * its test depend on this exact result type.
 */
export const useMarketOverview = (): UseQueryResult<MarketOverview> => {
  const { isOpen } = useMarketStatus()

  return useQuery({ ...marketOverviewQueryOptions(), refetchInterval: livePolling(isOpen) })
}
